import './feedback.scss';

import { ISOBSERVER } from '../../constants/global';
import { useGlobalContext } from '../../context/global';
import { type IAgentChatPanelData } from '../../interface/peer-chat';
import { type ChatUnreadTarget } from '../../lib/chat-unread';
import { PEER_PANELS_AVAILABLE } from '../../lib/peer-chat-flags';
import { counterpartOf } from '../../lib/peer-inbox';
import { FeedbackMenuSVG } from '../utils/svgs';

/**
 * The floating Help button.
 *
 * <p>Was the Feedback FAB. Its icon has always been a speech balloon, which was wrong for a
 * one-way form and is right for a chat — agents already read this button as "talk to
 * someone", and it now does that.</p>
 *
 * <p>Deliberately still ONE floating element. This FAB and the location-tracker pill already
 * share z-index 20 and avoid each other by hardcoded magic numbers (feedback.scss sets
 * bottom: 84px on mobile with a comment saying it clears the tracker). A third fixed element
 * would make that arrangement untenable, so the button grows an unread badge instead — which
 * is also the only affordance on mobile, where the label is hidden.</p>
 *
 * <p>The filename and `data-feedback-widget` are kept so feedback.scss and anything keyed on
 * that attribute keep working.</p>
 */
const FeedbackFab = () => {
	const { rsProps, supportChat, chatUnread } = useGlobalContext();

	// The aggregate when the layout provides it; the support count alone otherwise (tests and
	// any host that mounts the FAB without the peer plumbing).
	const total = chatUnread?.total ?? supportChat?.unread ?? 0;
	const conversations = chatUnread?.threadsWithUnread ?? (total > 0 ? 1 : 0);
	const target: ChatUnreadTarget = chatUnread?.target ?? (total > 0 ? { kind: 'support' } : { kind: 'hub' });

	/**
	 * With something waiting, go STRAIGHT to the conversation.
	 *
	 * <p>The hub is the right landing place when an agent is looking for something. It is the
	 * wrong one when someone is waiting on them: routing an unread message through a menu
	 * costs a second tap at exactly the moment the message is most urgent, and the badge has
	 * already told them where they are going. With unread in SEVERAL conversations a parent
	 * lands on the inbox, where "Waiting on you" is the first section.</p>
	 *
	 * <p>Deliberately not a separate toast or banner — this file's own constraint is that
	 * there is exactly one floating element, so the behaviour changes rather than the
	 * furniture.</p>
	 */
	const openHelp = () => {
		if (!rsProps) return;

		switch (target.kind) {
			case 'support':
				rsProps.callSection({ action: 'view', component: 'support-chat', title: 'Support desk' });
				return;
			case 'peer': {
				if (!PEER_PANELS_AVAILABLE) break;
				const me = supportChat?.me ?? 0;
				const other = counterpartOf(target.summary, me);
				const data: IAgentChatPanelData = {
					counterpart: {
						coreUserId: other.coreUserId,
						name: other.fullName || 'Agent',
						entityCode: other.entityCode,
						role: other.role,
					},
					electionId: target.summary.electionId,
					origin: 'fab',
				};
				// An observer's peer thread opens in the observer panel: agent-chat is party-only and
				// would mount nothing for them.
				rsProps.callSection({
					action: 'view',
					component: ISOBSERVER() ? 'observer-chat' : 'agent-chat',
					title: data.counterpart.name,
					data,
				});
				return;
			}
			case 'inbox':
				// Observers have no inbox panel (messages-inbox is party-only); the hub lists their threads.
				if (!PEER_PANELS_AVAILABLE || ISOBSERVER()) break;
				rsProps.callSection({ action: 'view', component: 'messages-inbox', title: 'Messages' });
				return;
			default:
				break;
		}

		rsProps.callSection({ action: 'view', component: 'help-hub', title: 'Help' });
	};

	const label =
		total > 0
			? conversations > 1
				? `Help, ${total} unread messages in ${conversations} conversations`
				: `Help, ${total} unread messages`
			: 'Help';

	return (
		<button
			type='button'
			className='feedback-fab'
			data-feedback-widget
			onClick={openHelp}
			aria-label={label}
		>
			<span className='feedback-fab__icon'>
				<FeedbackMenuSVG fill='#fff' />
			</span>
			<span className='feedback-fab__label'>Help</span>
			{total > 0 ? (
				<span
					className='feedback-fab__badge'
					aria-hidden='true'
				>
					{total > 9 ? '9+' : total}
				</span>
			) : null}
		</button>
	);
};

export default FeedbackFab;
